import type { WorkstationFuelNetworkSnapshot } from '@dreamgame/shared';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { getBurnRemainingMsFromServerTimeline } from './craftingPanelUtils';

export const useWorkstationFuelBurnProgress = ({
  fuel,
  currentServerTick,
  currentServerTickIntervalMs,
  currentServerTickUpdatedAtMs,
}: {
  fuel: WorkstationFuelNetworkSnapshot | null;
  currentServerTick: number;
  currentServerTickIntervalMs: number;
  currentServerTickUpdatedAtMs: number;
}): number => {
  const [displayBurnRemainingMs, setDisplayBurnRemainingMs] = useState<number>(() =>
    getBurnRemainingMsFromServerTimeline({
      fuel,
      currentServerTick,
      currentServerTickIntervalMs,
      currentServerTickUpdatedAtMs,
      nowMs: performance.now(),
    })
  );
  const burnAnimationRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (burnAnimationRef.current) {
      cancelAnimationFrame(burnAnimationRef.current);
      burnAnimationRef.current = 0;
    }

    const initialRemainingMs = getBurnRemainingMsFromServerTimeline({
      fuel,
      currentServerTick,
      currentServerTickIntervalMs,
      currentServerTickUpdatedAtMs,
      nowMs: performance.now(),
    });
    setDisplayBurnRemainingMs(initialRemainingMs);

    if (initialRemainingMs <= 0) {
      return;
    }

    const animateBurn = () => {
      const nextRemainingMs = getBurnRemainingMsFromServerTimeline({
        fuel,
        currentServerTick,
        currentServerTickIntervalMs,
        currentServerTickUpdatedAtMs,
        nowMs: performance.now(),
      });
      setDisplayBurnRemainingMs(nextRemainingMs);

      if (nextRemainingMs > 0) {
        burnAnimationRef.current = requestAnimationFrame(animateBurn);
      }
    };

    burnAnimationRef.current = requestAnimationFrame(animateBurn);

    return () => {
      if (burnAnimationRef.current) {
        cancelAnimationFrame(burnAnimationRef.current);
        burnAnimationRef.current = 0;
      }
    };
  }, [
    currentServerTick,
    currentServerTickIntervalMs,
    currentServerTickUpdatedAtMs,
    fuel?.burnDurationMs,
    fuel?.burnRemainingMs,
    fuel?.burnStartedAtTick,
  ]);

  return displayBurnRemainingMs;
};
